/**
 * @module CanvasView
 * @purpose Top-level canvas layout — wires useCanvasState to all child panels.
 * @depends useCanvasState, CanvasFilterTree, CanvasResultsArea, CanvasDetailPanel, CanvasTimelineDock
 * @dependedOnBy CaseAnalysisTab
 *
 * @rules
 * - Performance: all data fetching delegated to useCanvasState
 * - State: none beyond what useCanvasState exposes
 * - Errors: renders error/empty/loading states inline above the results area
 */

import { useTranslation } from 'react-i18next'
import { useCanvasState } from './use-canvas-state'
import { CanvasFilterTree } from './CanvasFilterTree'
import { CanvasResultsArea } from './CanvasResultsArea'
import { CanvasDetailPanel } from './CanvasDetailPanel'
import { CanvasTimelineDock } from './CanvasTimelineDock'
import { CanvasReasoningPanel } from './reasoning/CanvasReasoningPanel'
import type { DecoratedArtefact } from './types'

const CANVAS_MIN_HEIGHT = '600px'

const CANVAS_GRID_STYLE = {
  display: 'grid',
  gridTemplateColumns: 'auto 1fr auto',
  gridTemplateRows: '1fr auto',
} as const

export interface CanvasViewProps {
  caseId: string
}

function findArtefact(items: DecoratedArtefact[], selectedId: string | null): DecoratedArtefact | null {
  if (!selectedId) return null
  return items.find((item) => item.artefactId === selectedId) ?? null
}

function resolveEmptyKey(
  facets: { artefactTypes: { length: number } } | null,
  searchQuery: string,
): 'qcPending' | 'noSearchResults' | 'noResults' {
  if (!facets || facets.artefactTypes.length === 0) return 'qcPending'
  if (searchQuery.length > 0) return 'noSearchResults'
  return 'noResults'
}

interface CanvasStatusBannerProps {
  loading: boolean
  error: string | null
  isEmpty: boolean
  searchQuery: string
  facets: { artefactTypes: { length: number } } | null
  onRetry: () => void
}

function CanvasStatusBanner({
  loading,
  error,
  isEmpty,
  searchQuery,
  facets,
  onRetry,
}: CanvasStatusBannerProps): React.ReactElement | null {
  const { t } = useTranslation('analysis')

  if (loading) {
    return (
      <div role="status" aria-busy="true" className="px-4 py-3 text-sm" style={{ color: 'var(--text-muted)' }}>
        {t('canvas.searchPlaceholder')}
      </div>
    )
  }

  if (error) {
    return (
      <div role="alert" className="flex items-center gap-3 px-4 py-3 text-sm" style={{ color: 'var(--text)' }}>
        <span>{t('canvas.errorLoad')}</span>
        <button
          onClick={onRetry}
          className="text-xs font-[var(--font-mono)] uppercase tracking-wide underline hover:no-underline"
          style={{ color: 'var(--accent)' }}
        >
          {t('canvas.retry')}
        </button>
      </div>
    )
  }

  if (isEmpty) {
    const key = resolveEmptyKey(facets, searchQuery)
    const message =
      key === 'noSearchResults'
        ? t('canvas.empty.noSearchResults', { query: searchQuery })
        : t(`canvas.empty.${key}`)
    return (
      <div className="px-4 py-3 text-sm" style={{ color: 'var(--text-muted)' }}>
        {message}
      </div>
    )
  }

  return null
}

/**
 * Analysis canvas root layout.
 *
 * @behaviour Accepts caseId and passes it to useCanvasState for data fetching.
 *            Renders error/empty/loading banners above the results table.
 *            Filter tree built from live facets; timeline dock shows time-bucket bars.
 * @errors Displays error banner with retry on fetch failure.
 */
export function CanvasView({ caseId }: CanvasViewProps): React.ReactElement {
  const {
    items, loading, error, facets, facetsLoading,
    selectedFilters, searchQuery, activeView, selectedArtefactId,
    isExpanded, reasoningPanelOpen, refetch,
    setSearchQuery, setSelectedFilters, clearFilters, setActiveView,
    setSelectedArtefactId, toggleDock, toggleBookmark, toggleStar,
    onTagAction, setReasoningPanelOpen, drillToCluster,
  } = useCanvasState({ caseId })

  const selectedArtefact = findArtefact(items, selectedArtefactId)
  const isEmpty = !loading && !error && items.length === 0

  return (
    <div
      className="flex flex-col overflow-hidden rounded-lg border"
      style={{
        borderColor: 'var(--border)',
        background: 'var(--bg-card)',
        minHeight: CANVAS_MIN_HEIGHT,
      }}
    >
      <CanvasStatusBanner
        loading={loading && items.length === 0}
        error={error}
        isEmpty={isEmpty}
        searchQuery={searchQuery}
        facets={facetsLoading ? null : facets}
        onRetry={() => { void refetch() }}
      />
      <div className="flex-1 overflow-hidden" style={CANVAS_GRID_STYLE}>
        <CanvasFilterTree
          facets={facets}
          selectedFilters={selectedFilters}
          onFilterChange={setSelectedFilters}
          onClearAll={clearFilters}
        />
        <CanvasResultsArea
          caseId={caseId}
          artefacts={items}
          artefactsLoading={loading}
          searchQuery={searchQuery}
          activeView={activeView}
          selectedArtefactId={selectedArtefactId}
          attributeData={facets?.artefactTypes ?? []}
          selectedFilters={selectedFilters}
          reasoningPanelOpen={reasoningPanelOpen}
          onSearchChange={setSearchQuery}
          onViewChange={setActiveView}
          onRowSelect={setSelectedArtefactId}
          onBookmark={toggleBookmark}
          onStar={toggleStar}
          onTagAction={onTagAction}
          onReasoningToggle={setReasoningPanelOpen}
        />
        {reasoningPanelOpen
          ? <CanvasReasoningPanel caseId={caseId} onClose={() => { setReasoningPanelOpen(false) }} drillToCluster={drillToCluster} />
          : <CanvasDetailPanel artefact={selectedArtefact} onBookmark={toggleBookmark} />}
      </div>
      <CanvasTimelineDock
        isExpanded={isExpanded}
        onToggle={toggleDock}
        timelineBuckets={facets?.timeBuckets ?? []}
      />
    </div>
  )
}
